// Pure sale-option + renda-mínima logic for SaleModal (Phase 2).
//
// Extracted from the 1402-LOC SaleModal so the parcela-dropdown derivation and
// the income gate (Servopa rejects a sale when monthly income < 3× the parcela)
// are testable and the ×3 rule is single-source. Wraps the already-tested
// calcParcelaNormal / calcParcelaFlex (parcela-flex.ts).

import {
  calcParcelaNormal,
  calcParcelaFlex,
  FLEX_PERCENTAGES,
  type FlexPercentage,
} from "./parcela-flex";

export interface ParcelaOption {
  value: string; // 'normal' | `flex${pct}`
  label: string;
  amount: number;
  type: "normal" | "flex";
  flexPercentage?: number;
}

/** Category fields can arrive as number or string (DB/JSON). */
export interface ParcelaCategory {
  parcela_sem_seguro: number | string | null | undefined;
  valor_seguro: number | string | null | undefined;
}

const toNum = (v: number | string | null | undefined): number =>
  typeof v === "number" ? v : parseFloat(String(v || 0));

// Option label uses a plain "R$ 1234,56" (no thousand separator) — intentionally
// different from lib/format's formatBRL; preserve exactly.
const labelBRL = (v: number): string => `R$ ${v.toFixed(2).replace(".", ",")}`;

/**
 * Build the parcela dropdown options: a "Normal" option always, plus one per
 * flex percentage when `isFlex`. `hasInsurance` adds valor_seguro to each amount
 * and a " - Com seguro" suffix.
 */
export function getParcelaOptions(
  category: ParcelaCategory | null | undefined,
  { hasInsurance, isFlex }: { hasInsurance: boolean; isFlex: boolean },
): ParcelaOption[] {
  if (!category) return [];

  const parcelaSemSeguro = toNum(category.parcela_sem_seguro);
  const valorSeguro = toNum(category.valor_seguro);
  const seguroSuffix = hasInsurance ? " - Com seguro" : "";
  const flexInput = { parcelaSemSeguro, valorSeguro, hasInsurance };

  const normalAmount = calcParcelaNormal(flexInput);
  const options: ParcelaOption[] = [
    {
      value: "normal",
      label: `${labelBRL(normalAmount)} - Normal${seguroSuffix}`,
      amount: normalAmount,
      type: "normal",
    },
  ];

  if (isFlex) {
    FLEX_PERCENTAGES.forEach((percentage: FlexPercentage) => {
      const flexValue = calcParcelaFlex(flexInput, percentage);
      options.push({
        value: `flex${percentage}`,
        label: `${labelBRL(flexValue)} - Flex ${percentage}%${seguroSuffix}`,
        amount: flexValue,
        type: "flex",
        flexPercentage: percentage,
      });
    });
  }

  return options;
}

/** Servopa requires monthly income >= 3× the parcela. */
export const RENDA_MULTIPLIER = 3;

/** Minimum monthly income required to sell a parcela of `parcelaAmount`. */
export function rendaMinima(parcelaAmount: number): number {
  return parcelaAmount * RENDA_MULTIPLIER;
}

/** Whether `renda` meets the 3× minimum for `parcelaAmount`. */
export function checkRenda(renda: number, parcelaAmount: number): boolean {
  return renda >= rendaMinima(parcelaAmount);
}

/** Mensagem única da trava de vagas — os dois pontos de entrada da venda
 *  (GroupDetailModal e ProposalSimulationsPage) mostram o mesmo aviso. */
export const SEM_VAGAS_MSG =
  "Grupo sem vagas. Venda indisponível neste grupo.";

/**
 * Vagas disponíveis do grupo (servopa_groups_enriched.vacancies, texto vindo
 * do scraper — "12", "0", às vezes com lixo ao redor). Retorna null quando o
 * dado não existe ou não parseia: só bloqueamos venda com zero CONFIRMADO,
 * nunca por dado ausente.
 */
export function parseVagasDisponiveis(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : null;
  }
  if (typeof value !== "string") return null;

  const digits = (value.match(/\d[\d.,]*/)?.[0] || "").replace(/\D/g, "");
  if (!digits) return null;

  const parsed = Number.parseInt(digits, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Aviso de grupo quase cheio — a venda segue, mas sem seguro pode falhar. */
export const POUCAS_VAGAS_MSG =
  "Grupo quase cheio: venda sem seguro pode falhar.";

/** Limiar do aviso: vagas restantes em até 10% do total de cotas do grupo. */
export const POUCAS_VAGAS_PCT = 10;

/**
 * Grupo "quase cheio" = vagas restantes <= 10% do total de cotas.
 * `total` vem de servopa_group_details.numero_consorciados, que o scraper às
 * vezes traz corrompido (1, 5, valores menores que as próprias vagas) — em
 * ~1/3 dos grupos ativos. Sem total plausível o percentual não é confiável,
 * então retornamos false: aviso só com dado bom, venda nunca é atrapalhada
 * por dado ruim. Vagas zero/desconhecidas também não avisam (zero é caso de
 * bloqueio, tratado antes).
 */
export function isGrupoQuaseCheio(
  vagas: number | null,
  total: number | null | undefined,
): boolean {
  if (vagas == null || vagas <= 0) return false;
  if (total == null || total < vagas || total <= 10) return false;
  return vagas / total <= POUCAS_VAGAS_PCT / 100;
}
